import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import * as businessService from '../../data-layer/business/service';
import { BusinessStatus } from '../../mappers/business/toBusinessModel';
import { toBusinessUI } from '../../mappers/business/toBusinessUI';
import { BusinessCard } from '../../ui/business/BusinessCard/BusinessCard';
import { BusinessCardSkeleton } from '../../ui/business/BusinessCard/parts/BusinessCardSkeleton/BusinessCardSkeleton';
import { MessengerButton } from '../../ui/business/MessengerWidget';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { WithHint } from '../../ui/feedback/Tooltip';
import { IconKebab } from '../../ui/icon/IconKebab';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { Menu, MenuItem } from '../../ui/navigation/Menu';
import { Typography } from '../../ui/typography/Typography';
import { useEventSource } from '../../utils/LocationContext';
import {
    canEditBusiness,
    canRegisterBusiness,
    canSeeSupportChat,
    hasAccessToBusinessStat,
    isBlogger,
    isBloggerBusiness,
    isBusinessAdmin,
} from '../../utils/accessService';
import {
    analyticsService,
    BusinessTransitionType,
    EventSource,
} from '../../utils/analyticsService';
import { useIsMobile } from '../../utils/getIsMobile';
import {
    ymTrackAllPostClick,
    ymTrackChangeAvatar,
    ymTrackChangeCover,
    ymTrackChatClick,
    ymTrackOpenCatalogFromBusiness,
    ymTrackPublishedClick,
    ymTrackSaveAvatar,
    ymTrackSaveAvatarError,
    ymTrackSaveCover,
    ymTrackSaveCoverError,
} from '../../utils/metrika';
import { redirectToPersonalData } from '../../utils/redirectToPersonalData';
import { useBackButton } from '../../utils/useBackButton';
import { useIsAdvertisingCatalogEnabled } from '../../utils/useIsAdvertisingCatalogEnabled';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { DialogRoute, useOpenDialog } from '../ModalRoute';
import { ADVERTISING_CATALOG_URL, DIALOGS } from '../constants';
import { CpaContainer } from '../users/CpaContainer/CpaContainer';
import { UserSubscriptions } from '../users/UserSubscriptions';
import { useCurrentUser, useCurrentUserIsLoading } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { BusinessDetailsTabs } from './BusinessDetailsTabs/BusinessDetailsTabs';
import { BusinessImagePersistence } from './BusinessImagePersistence';
import { useBusinessCallbacks } from './useBusinessCallbacks';
import { useBusinessModel } from './useBusinessModel';
import type { BusinessModel, BusinessStatsModel } from '../../data-layer/business/types';

const DEFAULT_STAT_PERIOD = '7';

const buildMeta = (model: BusinessModel | undefined, canonicalUrl: string) => {
    if (!model) {
        return {};
    }

    return {
        title: `${model.displayName} • Яндекс Ритм`,
        description: model.description,
        links: [{ rel: 'canonical', href: canonicalUrl }],
        openGraph: {
            title: `Яндекс Ритм | ${model.publicId}`,
            description: model.description,
            image: model.avatarUrl,
        },
    };
};

interface BusinessStatisticProps {
    stats: BusinessStatsModel;
}

export const BusinessDetails = () => {
    const params = useParams();
    const businessId = params.businessId!;
    const openDialog = useOpenDialog();
    const business = useBusinessModel({ businessId });
    const currentUser = useCurrentUser();
    const businessCallbacks = useBusinessCallbacks();
    const { eventSource, prevEventSource } = useEventSource();
    const t = useTranslation();
    const isMobile = useIsMobile();
    const [businessStatistic, setBusinessStatistic] = useState<BusinessStatisticProps | undefined>(
        undefined,
    );
    const onNavigateBack = useBackButton();

    const navigate = useNavigate();

    const { loading: statisticLoading } = useAsync(async () => {
        setBusinessStatistic(undefined);
        if (hasAccessToBusinessStat(businessId, currentUser)) {
            setBusinessStatistic(
                await businessService.loadBusinessStatistic(businessId, DEFAULT_STAT_PERIOD),
            );
        }
    }, [businessId, currentUser]);

    const onBusinessTransition = useCallback((id: BusinessModel['id'], es: EventSource) => {
        analyticsService.trackBusinessTransition({
            businessId: id,
            eventSource: es,
            businessTransitionType: BusinessTransitionType.IN,
        });
    }, []);

    useEffect(() => {
        if (business?.id && eventSource === EventSource.BUSINESS) {
            onBusinessTransition(business.id, prevEventSource || EventSource.BUSINESS);
        }

        return () => {
            if (business?.id && eventSource === EventSource.BUSINESS) {
                analyticsService.trackBusinessTransition({
                    businessId: business.id,
                    eventSource: EventSource.BUSINESS,
                    businessTransitionType: BusinessTransitionType.OUT,
                });
            }
        };
    }, [business, eventSource, onBusinessTransition, prevEventSource]);

    const canonicalUrl = useCanonicalUrl();
    useMeta(buildMeta(business, canonicalUrl));

    const onReport = useCallback(() => {
        openDialog(DIALOGS.REPORT_DIALOG, { targetId: businessId, targetType: 'business' });
    }, [businessId, openDialog]);

    const onBadgeClick = useCallback(() => {
        if (business?.status === BusinessStatus.WITHOUT_RANGE) {
            openDialog(DIALOGS.WITHOUT_RANGE, {
                editLink: business?.editLink,
            });
        }
    }, [business?.status, business?.editLink, openDialog]);

    const onCatalogClick = useCallback(() => {
        analyticsService.trackOpenCatalog({
            businessId: business?.id,
            eventSource: EventSource.BUSINESS,
        });
        ymTrackOpenCatalogFromBusiness({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [business?.id, currentUser?.login, currentUser?.profile?.publicId]);

    const onTabClick = useCallback(
        (value: string) => {
            if (value === `/businesses/${businessId}`) {
                ymTrackPublishedClick({
                    page: 'Профиль бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
            }
            if (value === `/businesses/${businessId}/moderation`) {
                ymTrackAllPostClick({
                    page: 'Профиль бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
            }
        },
        [businessId, currentUser?.login, currentUser?.profile?.publicId],
    );

    const onPersistBusinessCover = useCallback(() => {
        ymTrackChangeCover({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        openDialog(DIALOGS.PERSISTENCE_COVER);
    }, [currentUser?.login, currentUser?.profile?.publicId, openDialog]);

    const onPersistBusinessAvatar = useCallback(() => {
        ymTrackChangeAvatar({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        if (isBloggerBusiness(business?.type)) {
            redirectToPersonalData();
        } else {
            openDialog(DIALOGS.PERSISTENCE_AVATAR);
        }
    }, [business, currentUser, openDialog]);

    const trackSuccessAvatarChange = useCallback(() => {
        ymTrackSaveAvatar({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackErrorAvatarChange = useCallback(() => {
        ymTrackSaveAvatarError({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackSuccessCoverChange = useCallback(() => {
        ymTrackSaveCover({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const trackErrorCoverChange = useCallback(() => {
        ymTrackSaveCoverError({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const onChatClick = useCallback(() => {
        ymTrackChatClick({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const contextMenuEnabled =
        business &&
        (!isBusinessAdmin(business.id, currentUser) ||
            canRegisterBusiness(currentUser?.businessRegistrationStatus));

    const adminContextMenuEnabled = business && isBusinessAdmin(business.id, currentUser);
    const isUserLoading = useCurrentUserIsLoading();
    const isAdvertisingCatalogEnabled = useIsAdvertisingCatalogEnabled();

    const contextMenuItems = useMemo(() => {
        if (contextMenuEnabled) {
            return (
                <>
                    <MenuItem onClick={() => businessCallbacks.onShare(business)}>
                        <Typography>{t('Поделиться')}</Typography>
                    </MenuItem>
                    {canRegisterBusiness(currentUser?.businessRegistrationStatus) &&
                    !isBloggerBusiness(business.type) ? (
                        <MenuItem
                            onClick={() =>
                                window.open(`${process.env.REACT_APP_REGISTRATION_LINK}`)
                            }
                        >
                            <Typography>{t('Подтвердить права на профиль')}</Typography>
                        </MenuItem>
                    ) : undefined}
                    {isBusinessAdmin(business.id, currentUser) ? undefined : (
                        <MenuItem onClick={onReport}>
                            <Typography noWrap color="error">
                                {t('Пожаловаться')}
                            </Typography>
                        </MenuItem>
                    )}
                </>
            );
        }

        if (adminContextMenuEnabled) {
            return (
                <>
                    {canSeeSupportChat(business.id, currentUser) && (
                        <MenuItem
                            onClick={() =>
                                navigate(
                                    `/${isBlogger(currentUser?.role) ? 'profile' : 'businesses'}/${
                                        business.publicId
                                    }/management`,
                                )
                            }
                        >
                            <Typography>{t('Управление постами')}</Typography>
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => navigate(business.statisticUrl)}>
                        <Typography>{t('Статистика')}</Typography>
                    </MenuItem>
                    {isAdvertisingCatalogEnabled ? (
                        <MenuItem onClick={() => navigate(ADVERTISING_CATALOG_URL)}>
                            <Typography>{t('Монетизация')}</Typography>
                        </MenuItem>
                    ) : null}
                </>
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business, currentUser, businessStatistic]);

    return (
        <DashboardPage
            position="relative"
            py={0}
            menuSlot={({ appMenuItem }) => (
                <Box>
                    <Menu
                        trigger={
                            <IconButton>
                                <IconKebab />
                            </IconButton>
                        }
                    >
                        {contextMenuItems}
                        {appMenuItem}
                    </Menu>
                </Box>
            )}
        >
            {business && (currentUser || !isUserLoading) && isMobile !== null ? (
                <BusinessCard
                    {...businessCallbacks}
                    business={toBusinessUI(business)}
                    onPersistBusinessCover={onPersistBusinessCover}
                    onPersistBusinessAvatar={onPersistBusinessAvatar}
                    user={currentUser}
                    businessStatistic={businessStatistic}
                    statisticLoading={statisticLoading}
                    onReport={onReport}
                    onBadgeClick={onBadgeClick}
                    onCatalogClick={onCatalogClick}
                    cpaButton={<CpaContainer business={toBusinessUI(business)} />}
                >
                    <BusinessDetailsTabs onTabClick={onTabClick} />
                </BusinessCard>
            ) : (
                <BusinessCardSkeleton mb={{ xs: 0.5, sm: 1 }} />
            )}
            <Box width="100%">
                <Outlet key={businessId} />

                {business && canSeeSupportChat(business.id, currentUser) ? (
                    <Box position="fixed" right={2} bottom={{ xs: 8, sm: 2 }}>
                        <MessengerButton onClick={onChatClick} />
                    </Box>
                ) : null}
                {business && canEditBusiness(business.id, currentUser) ? (
                    <>
                        <DialogRoute
                            name={DIALOGS.PERSISTENCE_AVATAR}
                            element={
                                <BusinessImagePersistence
                                    business={business}
                                    cropShape="round"
                                    property="avatarUrl"
                                    title={
                                        <WithHint message="Выбирайте качественное изображение размером не менее 150x150 px">
                                            Выберите фото профиля
                                        </WithHint>
                                    }
                                    trackSuccessChange={trackSuccessAvatarChange}
                                    trackError={trackErrorAvatarChange}
                                    size={{ width: 150, height: 150 }}
                                />
                            }
                        />

                        <DialogRoute
                            name={DIALOGS.PERSISTENCE_COVER}
                            element={
                                <BusinessImagePersistence
                                    business={business}
                                    property="coverUrl"
                                    title={
                                        <WithHint message="Выбирайте качественное изображение размером не менее 1920x1080 px">
                                            Выберите обложку профиля
                                        </WithHint>
                                    }
                                    size={{ width: 1920, height: 1080 }}
                                    trackSuccessChange={trackSuccessCoverChange}
                                    trackError={trackErrorCoverChange}
                                />
                            }
                        />
                    </>
                ) : null}
            </Box>
            <DialogRoute
                name={DIALOGS.SUBSCRIPTIONS}
                element={<UserSubscriptions onClose={onNavigateBack} />}
            />
        </DashboardPage>
    );
};
